import type { Icon } from '@onefootprint/icons';
import { cva } from 'class-variance-authority';
import Link from 'next/link';
import { forwardRef, memo } from 'react';
import TooltipWrapper from '../tooltip-wrapper/tooltip-wrapper';

export type NavLinkProps = {
  href: string;
  icon: Icon;
  selected: boolean;
  text: string;
  isCollapsed?: boolean;
  isNew?: boolean;
  annotationBadge?: React.ReactNode;
};

const navLinkStyles = cva(
  'flex items-center no-underline rounded-default relative transition-transform duration-fast ease-out active:scale-pressed motion-reduce:active:scale-100',
  {
    variants: {
      selected: {
        true: 'text-primary bg-senary/70',
        false: 'text-tertiary hover:text-primary hover:bg-hover',
      },
      isCollapsed: {
        true: 'size-7 justify-center mx-auto',
        false: 'h-8 md:h-7 px-1.5 gap-2 w-full',
      },
    },
    compoundVariants: [
      { selected: true, isCollapsed: false, className: 'text-label-3' },
      { selected: false, isCollapsed: false, className: 'text-body-3' },
    ],
  },
);

const NavLink = forwardRef<HTMLAnchorElement, NavLinkProps>(
  ({ href, icon: Icon, selected, text, isCollapsed, annotationBadge }, ref) => {
    const linkContent = (
      <Link href={href} className={navLinkStyles({ selected, isCollapsed })} ref={ref}>
        <Icon />
        {!isCollapsed && (
          <>
            {text}
            {annotationBadge}
          </>
        )}
      </Link>
    );

    return isCollapsed ? (
      <TooltipWrapper tooltipContent={text} disableHoverable={!isCollapsed}>
        {linkContent}
      </TooltipWrapper>
    ) : (
      linkContent
    );
  },
);

NavLink.displayName = 'NavLink';

export default memo(NavLink);
